/*
 * Clever Canary
 * https://clevercanary.com/
 *
 * Utility class for CC-specific Google Tag Manager functionality. 
 */

// App dependencies
import { GAAction } from "./ga-action.model";
import { GACategory } from "./ga-category.model";
import { GAEntityName } from "./ga-entity-name.model";
import { GAEntityType } from "./ga-entity-type.model";
import * as GTMService from "../gtm/gtm.service";
import { GADimensionsByHref } from "./ga-dimensions-by-href.model";

/**
 * Create and send a GA tracking event generated from the specified external link and label. Dimensions are calculated
 * from the specified URL.
 * 
 * @param {string} url
 * @param {string} linkText
 */
export function trackExternalLinkClicked(url, linkText) {

    const dimensions = createExternalLinkDimensions(url);
    GTMService.trackEvent(GACategory.EXTERNAL_LINK, GAAction.CLICK, linkText, dimensions);
}

/**
 * If there is an entity name configured for the specified URL, return it as is. If not and the URL links to a Terra
 * workspace, generate the entity name from the workspace name in the URL. If the URL links to dbGaP, generate the
 * entity name from the dbGaP ID. Otherwise, return unspecified.
 *
 * @param {string} url
 * @returns {string}
 */
function calculateEntityName(url) {

    const {entityName} = GADimensionsByHref[url] || {};
    if ( entityName ) {
        return entityName;
    }

    return GAEntityName.UNSPECIFIED;
}

/**
 * If there is an entity type configured for the specified URL, return it as is. If not and the URL links to a Terra
 * workspace, return the workspace entity type. If the URL links to dbGaP, return the study entity type. Otherwise
 * return unspecified.
 *
 * @param {string} url
 * @returns {string}
 */
function calculateEntityType(url) {

    const {entityType} = GADimensionsByHref[url] || {};
    if ( entityType ) {
        return entityType;
    }

    return GAEntityType.UNSPECIFIED;
}

/**
 * Returns dimension values that are required when tracking click event: entity name, entity type, entity URL and 
 * source URL.
 *
 * @param {string} url
 * @returns {any}
 */
function createExternalLinkDimensions(url) {

    return {
        entityName: calculateEntityName(url),
        entityType: calculateEntityType(url),
        entityUrl: url,
        sourceUrl: window.location.pathname
    };
}
